/**
 * TimelineFilters Component
 *
 * Filter controls for the project timeline.
 * Includes date range, event type, and milestone filters.
 *
 * @module components/timeline/TimelineFilters
 */

import React, { useState } from 'react';
import { Row, Col, Form, Button, Collapse } from 'react-bootstrap';
import { getEventTypeOptions } from '../../services/timelineService';

/**
 * TimelineFilters component
 * @param {Object} props
 * @param {Object} props.filters - Current filter values
 * @param {Function} props.onFilterChange - Callback when filters change
 * @param {Function} props.onClearFilters - Callback to clear all filters
 */
const TimelineFilters = ({ filters, onFilterChange, onClearFilters }) => {
  const [showFilters, setShowFilters] = useState(false);
  const eventTypeOptions = getEventTypeOptions();

  const handleChange = (field, value) => {
    onFilterChange({
      ...filters,
      [field]: value,
    });
  };

  const hasActiveFilters = filters.eventTypes?.length > 0 ||
    filters.startDate ||
    filters.endDate ||
    filters.milestonesOnly;

  return (
    <div className="timeline-filters mb-3">
      {/* Toggle button for mobile */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => setShowFilters(!showFilters)}
          aria-expanded={showFilters}
          className="d-md-none"
        >
          <i className={`bi ${showFilters ? 'bi-chevron-up' : 'bi-filter'} me-1`}></i>
          Filtros
          {hasActiveFilters && (
            <span className="badge bg-primary ms-1">!</span>
          )}
        </Button>

        {hasActiveFilters && (
          <Button
            variant="link"
            size="sm"
            onClick={onClearFilters}
            className="text-danger"
          >
            <i className="bi bi-x-circle me-1"></i>
            Limpiar Filtros
          </Button>
        )}
      </div>

      {/* Filters - collapsible on mobile, always visible on desktop */}
      <Collapse in={showFilters} className="d-md-block">
        <div>
          <Row className="g-2">
            {/* Date Range */}
            <Col xs={12} md={3}>
              <Form.Group>
                <Form.Label className="small mb-1">Desde</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={filters.startDate || ''}
                  onChange={(e) => handleChange('startDate', e.target.value || null)}
                />
              </Form.Group>
            </Col>

            <Col xs={12} md={3}>
              <Form.Group>
                <Form.Label className="small mb-1">Hasta</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={filters.endDate || ''}
                  onChange={(e) => handleChange('endDate', e.target.value || null)}
                />
              </Form.Group>
            </Col>

            {/* Event Type */}
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label className="small mb-1">Tipo de Evento</Form.Label>
                <Form.Select
                  size="sm"
                  value={filters.eventTypes?.[0] || ''}
                  onChange={(e) => handleChange('eventTypes', e.target.value ? [e.target.value] : null)}
                >
                  <option value="">Todos los tipos</option>
                  {eventTypeOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            {/* Milestones Only */}
            <Col xs={12} md={2}>
              <Form.Group>
                <Form.Label className="small mb-1 d-block">&nbsp;</Form.Label>
                <Form.Check
                  type="switch"
                  id="milestones-only"
                  label="Solo Hitos"
                  checked={filters.milestonesOnly || false}
                  onChange={(e) => handleChange('milestonesOnly', e.target.checked)}
                />
              </Form.Group>
            </Col>
          </Row>
        </div>
      </Collapse>
    </div>
  );
};

export default TimelineFilters;
